// quote-notices.ts — quote footer notices. Pure module.
//
// Every quote PDF carries three notices at the bottom: production, packaging and back-planning.
// The engine composes them as language-free messages; the display layer translates them.
//
// Pure: no current date, no recomputed counter — everything comes in as a parameter, so a frozen
// quote composes the same notices from its snapshot forever.

import { engineMessage, keyParam } from "../core/messages";

/**
 * Formats an ISO date as DD/MM/YYYY; a non-ISO value is returned unchanged.
 * Structural transform (document date shape), not locale vocabulary.
 * @param iso The ISO date string.
 * @returns The date as DD/MM/YYYY, or the input when it is not an ISO date.
 */
const formatDocDate = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

/** Packaging notice key (client commitment — wording lives in fr.json). */
export const PACKAGING_NOTICE = engineMessage("enginePackagingNotice");

/** The fixed back-planning rule key. */
export const BAT_LEAD_TIME_RULE = engineMessage("engineBatLeadTimeRule");

/**
 * Production notice: the production summary on one line, using the engine's counters.
 * @param c The engine counters (m², elements, files).
 * @returns The encoded production notice.
 */
export function productionNotice(c: { totalM2: number; elementCount: number; fileCount: number }): string {
  return engineMessage("engineProductionNotice", {
    m2: c.totalM2,
    elementCount: c.elementCount,
    elementUnit: keyParam(c.elementCount >= 2 ? "engineWordElements" : "engineWordElement"),
    fileCount: c.fileCount,
    fileUnit: keyParam(c.fileCount >= 2 ? "engineWordFiles" : "engineWordFile"),
  });
}

/**
 * Back-planning notice: the delivery date (or the range when lines carry different dates),
 * followed by the fixed rule. With no date at all the rule is still stated.
 * @param args The quote's overall delivery date and the per-line dates.
 * @returns The encoded back-planning notice.
 */
export function backPlanningNotice(args: {
  /** The quote's overall delivery date (`devis.date_livr`), ISO. */
  deliveryDate?: string | null;
  /** Per-line dates (multi-delivery); null entries are ignored. */
  lineDates?: readonly (string | null | undefined)[];
}): string {
  const dates = [
    ...(args.deliveryDate ? [args.deliveryDate] : []),
    ...(args.lineDates ?? []).filter((d): d is string => !!d),
  ]
    .map((d) => d.slice(0, 10))
    .sort();
  const lead = keyParam("engineBatLeadTimeRule");
  if (!dates.length) {
    return engineMessage("engineBackPlanNotice", { lead });
  }
  const first = dates[0]!;
  const last = dates[dates.length - 1]!;
  if (first === last) {
    return engineMessage("engineBackPlanNoticeSingle", { date: formatDocDate(first), lead });
  }
  return engineMessage("engineBackPlanNoticeRange", {
    from: formatDocDate(first),
    to: formatDocDate(last),
    lead,
  });
}

/**
 * The three notices, in order: production, packaging, back-planning.
 * @param args The engine counters, the overall delivery date and the per-line dates.
 * @returns The three footer notices (encoded messages).
 */
export function quoteFooterNotices(args: {
  counters: { totalM2: number; elementCount: number; fileCount: number };
  deliveryDate?: string | null;
  lineDates?: readonly (string | null | undefined)[];
}): string[] {
  return [
    productionNotice(args.counters),
    PACKAGING_NOTICE,
    backPlanningNotice({
      ...(args.deliveryDate !== undefined ? { deliveryDate: args.deliveryDate } : {}),
      ...(args.lineDates !== undefined ? { lineDates: args.lineDates } : {}),
    }),
  ];
}
